import { GridDashboardChartItem, HistoricalUtrs } from '@g17eco/types/insight-custom-dashboard';
import { SingleValue } from '@routes/custom-dashboard/items/charts/multi-utrs-chart/SingleValue';
import { CurrencyConfig, EMPTY_CURRENCY_CONFIG } from '@routes/custom-dashboard/items/charts/multi-utrs-chart/utils';
import { UtrvInitiativeRef } from '@g17eco/types/universalTrackerValue';
import { collectTableInitiatives, sliceUtrsDataByInitiative } from '../tables/utils';

type Props = Pick<GridDashboardChartItem, 'calculation' | 'unitText' | 'variables' | 'note'> & {
  utrsData: HistoricalUtrs[];
  hasSparkLine?: boolean;
  currencyConfig: CurrencyConfig | undefined;
};

type InitiativeSlice = UtrvInitiativeRef & {
  utrsData: HistoricalUtrs[];
};

const sliceByInitiative = (utrsData: HistoricalUtrs[]): InitiativeSlice[] =>
  collectTableInitiatives(utrsData).map((initiative) => ({
    ...initiative,
    utrsData: sliceUtrsDataByInitiative(utrsData, initiative._id),
  }));

export const DisaggregatedSingleValueGrid = (props: Props) => {
  const { utrsData, hasSparkLine = false, calculation, unitText, variables, note, currencyConfig } = props;
  const slices = sliceByInitiative(utrsData);
  const effectiveCurrencyConfig = currencyConfig ?? EMPTY_CURRENCY_CONFIG;

  if (!slices.length) {
    return (
      <div
        className='w-100 h-100 d-flex align-items-center justify-content-center text-ThemeTextLight'
        data-testid='disaggregated-single-value-empty'
      >
        No data available
      </div>
    );
  }

  // Responsive grid: widget width defines how many tiles fit per row. `auto-fit` + `minmax` lets
  // Google Charts size each tile so the per-tile sparkline + number stays legible.
  return (
    <div
      className='w-100 h-100 d-grid'
      style={{
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '0.75rem',
        padding: '0.25rem',
      }}
      data-testid='disaggregated-single-value-grid'
    >
      {slices.map((slice) => (
        <div
          key={slice._id}
          className='d-flex flex-column border border-ThemeBorderDefault rounded p-2'
          data-testid='disaggregated-single-value-tile'
          style={{ minHeight: 0 }}
        >
          <div
            className='text-sm text-center text-ThemeTextLight text-truncate mb-1'
            title={slice.name}
          >
            {slice.name}
          </div>
          <div className='flex-fill d-flex flex-column'>
            <SingleValue
              utrsData={slice.utrsData}
              variables={variables}
              calculation={calculation}
              unitText={unitText}
              note={note}
              hasSparkLine={hasSparkLine}
              readOnly={true}
              altUnitText=''
              currencyConfig={effectiveCurrencyConfig}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
